import { Display } from "rot-js"

import { TapestryArt } from "../data/ascii"
import { Color, RGBColor } from "../defines/colors"
import { Direction, PaletteColor, PlayerAction } from "../defines/enums"
import {
  BindablePigment,
  BlinkPosition,
  Offset,
  Position,
  RGB,
  TapestryStats,
} from "../defines/types"
import { EngineDefaults, TapestryDefaults } from "../defines/values"
import { BLACK_TILE, BORDER_TILE, TapestryTile } from "../map/tile"
import {
  getHexForPaletteColor,
  getOutputStringForColor,
  paletteColorToRGB,
  rgbToPaletteColor,
  toDisplayString,
} from "../utils/color"
import { shuffle } from "../utils/misc"
import { generateRandomNumber } from "../utils/rng"
import { Timer } from "../utils/timer"
import { Popup } from "./popup"

export class Tapestry extends Popup {
  public readonly VERSION = 1.0
  public static readonly PADDING = 2

  private _percentCompleted: number = 0
  private _tilesCompleted: number = 0
  private _totalColorTiles: number = 0
  private _tapestryComplete: boolean = false

  private colorCounts = {
    red: {
      have: 0,
      need: 0,
    },
    green: {
      have: 0,
      need: 0,
    },
    blue: {
      have: 0,
      need: 0,
    },
    magenta: {
      have: 0,
      need: 0,
    },
    yellow: {
      have: 0,
      need: 0,
    },
    cyan: {
      have: 0,
      need: 0,
    },
  }

  private _tiles: TapestryTile[][] = []

  private _statics: Position[] = []
  private _blinks: BlinkPosition[][] = []
  private _nextBlinkGroup: number = 0
  private _nextBlinkColor: number = 0

  private static TIMER_INCREMENT = 500
  private _blinkTimer: Timer | undefined = undefined

  private _selectedPigment: number = 0
  private _bindablePigments: BindablePigment[] = []
  private _availableOptions: number = 0

  private _display: Display | undefined

  constructor(offsets: Offset, name: string) {
    super(
      EngineDefaults.TROPHY_CASE_WIDTH,
      EngineDefaults.TROPHY_CASE_HEIGHT,
      offsets,
      name
    )

    this.initTiles()
    this.initTapestryArt()
    this.initStatics()

    this._totalColorTiles = this.computeTotalColorTiles()
  }

  get complete(): boolean {
    return this._tapestryComplete
  }

  get percentCompleted(): number {
    return this._percentCompleted
  }

  get tilesCompleted(): number {
    return this._tilesCompleted
  }

  get totalColorTiles(): number {
    return this._totalColorTiles
  }

  set display(display: Display) {
    this._display = display
  }

  getStats(): TapestryStats {
    return {
      percentCompleted: this._percentCompleted,
      tilesCompleted: this._tilesCompleted,
      totalColorTiles: this._totalColorTiles,
    }
  }

  private computeTotalColorTiles(): number {
    return (
      this.colorCounts.red.have +
      this.colorCounts.blue.have +
      this.colorCounts.green.have +
      this.colorCounts.magenta.have +
      this.colorCounts.yellow.have +
      this.colorCounts.cyan.have
    )
  }

  protected initTiles(): void {
    this._tiles = new Array(this.height)
    for (let y = 0; y < this.height; y++) {
      const row = new Array(this.width)
      for (let x = 0; x < this.width; x++) {
        const isWall =
          x === 0 || x === this.width - 1 || y === 0 || y === this.height - 1
        row[x] = isWall ? { ...BORDER_TILE } : { ...BLACK_TILE }
      }
      this._tiles[y] = row
    }
  }

  private initPigment(id: PaletteColor, pigment: RGB): RGB {
    let newColor = pigment
    newColor.a = 0.0
    return newColor
  }

  private initTapestryArt() {
    for (let y = 0; y < TapestryArt.length; ++y) {
      let col = 0
      for (let x = 0; x < TapestryArt[y].length; ++x) {
        if (TapestryArt[y][x] != " ") {
          const tile = this.getTapestryTile(TapestryArt[y].substring(x, x + 2))
          this._tiles[y][col] = tile
          x += 1
        }
        col += 1
      }
    }
  }

  private initStatics(): void {
    this._tiles.forEach((row, y) => {
      row.forEach((_, x) => {
        this._statics.push({ x: x, y: y })
      })
    })
  }

  bindPigmentToTiles(color: PaletteColor): void {
    const rgbColor = paletteColorToRGB(color)
    if (rgbColor !== undefined) {
      const tiles = this.findRandomTilesOfRGB(
        rgbColor,
        TapestryDefaults.TILES_PER_BIND
      )
      if (tiles !== undefined) {
        for (let i = 0; i < tiles.length; ++i) {
          const pos = tiles[i]
          const updateTile = this._tiles[pos.y][pos.x]
          if (updateTile !== undefined && updateTile.fg.a !== undefined) {
            updateTile.fg.a = 1.0
            const color = rgbToPaletteColor(
              updateTile.fg.r,
              updateTile.fg.g,
              updateTile.fg.b
            )
            if (color !== undefined) {
              this.decrementNeed(color)
            }
          }
        }
      }

      this.computeCompletion()
      this.prepareToRender()
    }
  }

  decrementNeed(color: PaletteColor): void {
    switch (color) {
      case PaletteColor.RED:
        this.colorCounts.red.need -= 1
        break
      case PaletteColor.GREEN:
        this.colorCounts.green.need -= 1
        break
      case PaletteColor.BLUE:
        this.colorCounts.blue.need -= 1
        break
      case PaletteColor.YELLOW:
        this.colorCounts.yellow.need -= 1
        break
      case PaletteColor.MAGENTA:
        this.colorCounts.magenta.need -= 1
        break
      case PaletteColor.CYAN:
        this.colorCounts.cyan.need -= 1
        break
    }
  }

  findRandomTilesOfRGB(color: RGB, count: number): Position[] | undefined {
    let matchingTiles: Position[] = []
    this._tiles.forEach((row, y) => {
      row.forEach((tile, x) => {
        const c = tile.fg
        if (c.r == color.r && c.g == color.g && c.b == color.b) {
          if (c.a !== undefined && c.a < 1.0) {
            matchingTiles.push({ x: x, y: y })
          }
        }
      })
    })

    if (matchingTiles.length > 0) {
      shuffle(matchingTiles)
      return matchingTiles.slice(0, count)
    }

    return undefined
  }

  fillTapestry(): void {
    for (let i = 0; i < this._tiles.length; ++i) {
      for (let j = 0; j < this._tiles[i].length; ++j) {
        const updateTile = this._tiles[i][j]
        if (updateTile !== undefined && updateTile.fg.a !== undefined) {
          updateTile.fg.a = 1.0
        }
      }
    }

    this.computeCompletion()
    this.prepareToRender()
  }

  clearTapestry(): void {
    for (let i = 0; i < this._tiles.length; ++i) {
      for (let j = 0; j < this._tiles[i].length; ++j) {
        const updateTile = this._tiles[i][j]
        if (updateTile && updateTile.fg.a) {
          updateTile.fg.a = 0.0
        }
      }
    }

    this._blinks = []
    if (this._blinkTimer !== undefined) {
      this._blinkTimer.stop()
      this._blinkTimer = undefined
    }
    this.initStatics()
  }

  private getTapestryTile(code: string): TapestryTile {
    const newSymbol = code[1]
    let fg: RGB = RGBColor.TAPESTRY_NOTHING

    switch (code[0]) {
      case "y":
        this.colorCounts.yellow.have += 1
        this.colorCounts.yellow.need += 1
        fg = this.initPigment(PaletteColor.YELLOW, RGBColor.YELLOW)
        break
      case "r":
        this.colorCounts.red.have += 1
        this.colorCounts.red.need += 1
        fg = this.initPigment(PaletteColor.RED, RGBColor.RED)
        break
      case "m":
        this.colorCounts.magenta.have += 1
        this.colorCounts.magenta.need += 1
        fg = this.initPigment(PaletteColor.MAGENTA, RGBColor.MAGENTA)
        break
      case "b":
        this.colorCounts.blue.have += 1
        this.colorCounts.blue.need += 1
        fg = this.initPigment(PaletteColor.BLUE, RGBColor.BLUE)
        break
      case "c":
        this.colorCounts.cyan.have += 1
        this.colorCounts.cyan.need += 1
        fg = this.initPigment(PaletteColor.CYAN, RGBColor.CYAN)
        break
      case "g":
        this.colorCounts.green.have += 1
        this.colorCounts.green.need += 1
        fg = this.initPigment(PaletteColor.GREEN, RGBColor.GREEN)
        break
      default:
        break
    }

    return {
      symbol: newSymbol,
      fg: { ...fg },
      bg: RGBColor.BLACK,
    }
  }

  private blink(): void {
    if (this._display) {
      const color = TapestryDefaults.BLINK_COLORS[this._nextBlinkColor]
      const group = this._blinks[this._nextBlinkGroup]
      group.forEach((b, idx) => {
        const tile = this._tiles[b.y][b.x]

        if (this._display !== undefined) {
          this._display.draw(
            b.x + this.offsets.xOffset,
            b.y + this.offsets.yOffset,
            tile.symbol,
            toDisplayString(color),
            toDisplayString(tile.bg)
          )
        }
      })

      this._nextBlinkGroup += 1
      if (this._nextBlinkGroup >= this._blinks.length) {
        this._nextBlinkGroup = 0
      }

      this._nextBlinkColor += 1
      if (this._nextBlinkColor >= TapestryDefaults.BLINK_COLORS.length) {
        this._nextBlinkColor = 0
      }
    }
  }

  private startBlinkTimer(): void {
    if (this._blinkTimer !== undefined) {
      this._blinkTimer.start(true, () => {
        this.blink()
      })
    }
  }

  prepareToRender(): void {
    if (this._tapestryComplete) {
      window.engine.changeMusic(TapestryDefaults.MUSIC, false, true)

      if (this._blinks.length == 0) {
        this._statics = []
        this._blinks = new Array(TapestryDefaults.BLINK_GROUPS)
        for (let i = 0; i < this._blinks.length; ++i) {
          this._blinks[i] = []
        }

        if (this._blinkTimer === undefined) {
          this._blinkTimer = new Timer(Tapestry.TIMER_INCREMENT)
        }

        this._tiles.forEach((row, y) => {
          row.forEach((t, x) => {
            if (t.symbol == TapestryDefaults.BLINK_SYMBOL) {
              const idx = generateRandomNumber(0, this._blinks.length - 1)
              this._blinks[idx].push({ x: x, y: y, on: true })
            } else {
              this._statics.push({ x: x, y: y })
            }
          })
        })
      }
      this.startBlinkTimer()

      return
    }

    this.prepareSelectablePigments()
  }

  postRender(): void {
    if (this._blinkTimer !== undefined) {
      this._blinkTimer.stop()
    }
  }

  private computeTilesCompleted(): number {
    let tilesCompleted = 0
    this._tiles.forEach((row) => {
      row.forEach((t) => {
        if (t.symbol != " " && t.fg.a !== undefined && t.fg.a >= 1.0) {
          tilesCompleted += 1
        }
      })
    })
    return tilesCompleted
  }

  private computeCompletion(): void {
    if (this._tapestryComplete) {
      return
    }

    this._tilesCompleted = this.computeTilesCompleted()
    this._percentCompleted = (this._tilesCompleted / this.totalColorTiles) * 100
    if (this._percentCompleted >= 100.0) {
      this._tapestryComplete = true
    }
  }

  private prepareSelectablePigments(): void {
    const player = window.engine.getPlayer()
    this._bindablePigments = player.getBindablePigments()
    this._availableOptions = 0
    this._bindablePigments.forEach((p) => {
      if (p.bindable && this.needsPigment(p.color)) {
        this._availableOptions += 1
      }
    })
  }

  needsPigment(color: PaletteColor): boolean {
    switch (color) {
      case PaletteColor.RED:
        return this.colorCounts.red.need > 0
      case PaletteColor.GREEN:
        return this.colorCounts.green.need > 0
      case PaletteColor.BLUE:
        return this.colorCounts.blue.need > 0
      case PaletteColor.YELLOW:
        return this.colorCounts.yellow.need > 0
      case PaletteColor.MAGENTA:
        return this.colorCounts.magenta.need > 0
      case PaletteColor.CYAN:
        return this.colorCounts.cyan.need > 0
    }
    return false
  }

  canSelectPigment(): boolean {
    return this._availableOptions > 0
  }

  render(display: Display) {
    // this.debugColorCounts()

    window.engine.sendMessage("Viewing " + this.name, false)

    this._statics.forEach((pos) => {
      const tile = this._tiles[pos.y][pos.x]
      display.draw(
        pos.x + this.offsets.xOffset,
        pos.y + this.offsets.yOffset,
        tile.symbol,
        toDisplayString(tile.fg),
        toDisplayString(tile.bg)
      )
    })

    const percentage = this._percentCompleted.toFixed(1) + "%"
    const colors = "%c{#fff}%b{#000}"
    display.drawText(
      this.width - percentage.length - 1,
      this.height - 2,
      colors + percentage
    )

    if (this._showConfirmDialog) {
      if (this._availableOptions == 0) {
        const message: string =
          "Unable to bind pigments at present. N|n or ESC to cancel"
        const blank: string = ".".padStart(message.length, ".")
        display.drawText(10, 12, `%c{#000}%b{#000}${blank}`)
        display.drawText(10, 13, `%c{#fff}%b{#000}${message}`)
        display.drawText(10, 14, `%c{#000}%b{#000}${blank}`)
      } else {
        let optionString: string = ""
        let optionStringActualLength: number = 0
        this._bindablePigments.forEach((pigment, idx) => {
          let colorString: string = getOutputStringForColor(pigment.color)
          optionStringActualLength += colorString.length + 2

          optionString += `%c{#000}%b{#000}.`
          if (pigment.bindable) {
            optionString += `%c{${getHexForPaletteColor(pigment.color)}}`
            if (idx == this._selectedPigment) {
              optionString += `%b{#b4b4b4}`
            } else {
              optionString += `%b{#000}`
            }
            optionString += colorString
          } else {
            optionString += `%c{${Color.DARK_GRAY}}%b{#000}${colorString}`
          }
          optionString += `%c{#000}%b{#000}.`
        })

        const blank: string = ".".padStart(optionStringActualLength, ".")
        display.drawText(18, 11, `%c{#000}%b{#000}${blank}`)

        display.drawText(18, 12, optionString)
        display.drawText(
          18 + optionStringActualLength - 1,
          12,
          `%c{#000}%b{#000}.`
        )

        display.drawText(18, 13, `%c{#000}%b{#000}${blank}`)
        display.drawText(18, 14, `%c{#000}%b{#000}.`)

        let confirmStr: string = "Y|y to bind selected pigment"
        display.drawText(19, 14, `%c{#fff}%b{#000}${confirmStr}`)
        let padding: string = ".".padEnd(
          optionStringActualLength - confirmStr.length - 1,
          "."
        )
        display.drawText(
          19 + confirmStr.length,
          14,
          `%c{#000}%b{#000}${padding}`
        )

        display.drawText(18, 15, `%c{#000}%b{#000}${blank}`)
        display.drawText(18, 16, `%c{#000}%b{#000}.`)

        confirmStr = "N|n or ESC to cancel"
        display.drawText(19, 16, `%c{#fff}%b{#000}${confirmStr}`)
        padding = ".".padEnd(
          optionStringActualLength - confirmStr.length - 1,
          "."
        )
        display.drawText(
          19 + confirmStr.length,
          16,
          `%c{#000}%b{#000}${padding}`
        )

        display.drawText(18, 17, `%c{#000}%b{#000}${blank}`)
      }
    }
  }

  update(action: PlayerAction): void {
    return
  }

  changePigmentSelection(direction: Direction): void {
    if (direction == Direction.LEFT) {
      do {
        this._selectedPigment -= 1
        if (this._selectedPigment < 0) {
          this._selectedPigment = this._bindablePigments.length - 1
        }
      } while (!this._bindablePigments[this._selectedPigment].bindable)
    } else if (direction == Direction.RIGHT) {
      do {
        this._selectedPigment += 1
        if (this._selectedPigment >= this._bindablePigments.length) {
          this._selectedPigment = 0
        }
      } while (!this._bindablePigments[this._selectedPigment].bindable)
    }
  }

  getSelectedPigment(): PaletteColor {
    return this._bindablePigments[this._selectedPigment].color
  }
}
